// cond - short for conditional - is an n-ary version of the trinary operator
//
// Wikipedia: https://en.wikipedia.org/wiki/Lisp_(programming_language)#Control_structures
//
// Example usage:
//
// const style = ({ isErrored, isSelected }) => ({
//   backgroundColor: cond(
//     [isSelected, Colors.blue500],
//     [isErrored, Colors.red100],
//     Colors.storm
//   )
// });

type Test = boolean | (() => boolean);
type Condition<T> = [Test, T] | T;
type Conditions<T> = Array<Condition<T>>;

const passes = <T>(condition: Condition<T>): T | boolean => {
  if (Array.isArray(condition)) {
    const [test] = condition;
    return (
      (test instanceof Function && test.apply(null)) ||
      (test && !(test instanceof Function))
    );
  }
  return condition;
};

const value = <T>(condition: Condition<T>): T => {
  if (Array.isArray(condition)) {
    const [, val] = condition;
    return val;
  }
  return condition;
};

const cond = <T>(...conditions: Conditions<T>): T =>
  (conditions && value(conditions.find(passes))) ||
  value(conditions[conditions.length - 1]);

export default cond;
